import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import Swal from 'sweetalert2'
import money from 'v-money3'
import { createApp } from 'vue'
import VueGtag from 'vue-gtag'
import { LoadingPlugin } from 'vue-loading-overlay'
import VueTheMask from 'vue-the-mask'

import ImoAccordion from '@/components/accordion/ImoAccordion.vue'
import ImoAlert from '@/components/alert/ImoAlert.vue'
import ImoBadge from '@/components/badges/ImoBadge.vue'
import ImoButton from '@/components/buttons/ImoButton.vue'
import ImoButtonGroup from '@/components/buttons/ImoButtonGroup.vue'
import ImoTextButton from '@/components/buttons/ImoTextButton.vue'
import ImoDropdown from '@/components/dropdown/ImoDropdown.vue'
import ImoMenuItem from '@/components/dropdown/ImoMenuItem.vue'
import HeroBlock from '@/components/heroblock/HeroBlock.vue'
import ImoIcon from '@/components/icons/ImoIcon.vue'
import ImoSpinner from '@/components/loading/ImoSpinner.vue'
import Segment from '@/components/menu/Segment.vue'
import SegmentedControl from '@/components/menu/SegmentedControl.vue'
import ImoModal from '@/components/modal/ImoModal.vue'
import ImoModal_ from '@/components/modal/ImoModal_.vue'
import SidePanel from '@/components/modal/SidePanel.vue'
import NotificationPopUp from '@/components/notification/NotificationPopUp.vue'
import ImoPagination from '@/components/pagination/ImoPagination.vue'
import ImoProgress from '@/components/progress/ImoProgress.vue'
import ImoFilter from '@/components/tables/ImoFilter.vue'
import ImoTable from '@/components/tables/ImoTable.vue'
import ImoTableV2 from '@/components/tables/ImoTableV2.vue'
import ImoTab from '@/components/tabs/ImoTab.vue'
import ImoTabs from '@/components/tabs/ImoTabs.vue'
import clickOutside from '@/plugins/clickOutside'
import ImoTooltip from '@/plugins/ImoTooltip'
import { useAuthStore } from '@/stores/Auth'

import App from './App.vue'
import ToastPlugin from './components/toast/ImoToastPlugin.js'
import { router } from './routes'
import {
  setupInterceptors,
  urlNextExceptions,
  generateCleanNextQuery,
} from './services/interceptors'

// App
const app = createApp(App)

// Pinia
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)

// Plugins Vue
app.use(router)
app.use(VueTheMask)
app.use(money, { precision: 2 })
app.use(LoadingPlugin)
app.use(ToastPlugin, { maxToasts: 5 })
app.use(
  VueGtag,
  {
    config: {
      id: 'G-8R7EPCMZGB',
      params: {
        send_page_view: false,
      },
    },
  },
  router,
)

// Diretivas
app.directive('tooltip', ImoTooltip)
app.directive('click-outside', clickOutside)

// Componentes globais
app.component('ImoIcon', ImoIcon)
app.component('HeroBlock', HeroBlock)
app.component('ImoButton', ImoButton)
app.component('ImoTextButton', ImoTextButton)
app.component('ImoAlert', ImoAlert)
app.component('ImoBadge', ImoBadge)
app.component('ImoAccordion', ImoAccordion)
app.component('ImoTabs', ImoTabs)
app.component('ImoTab', ImoTab)
app.component('ImoDropdown', ImoDropdown)
app.component('ImoMenuItem', ImoMenuItem)
app.component('ImoModal', ImoModal)
app.component('ImoButtonGroup', ImoButtonGroup)
app.component('ImoModal_', ImoModal_)
app.component('SidePanel', SidePanel)
app.component('ImoPagination', ImoPagination)
app.component('ImoFilter', ImoFilter)
app.component('ImoTable', ImoTable)
app.component('ImoTableV2', ImoTableV2)
app.component('ImoProgress', ImoProgress)
app.component('NotificationPopUp', NotificationPopUp)
app.component('SegmentedControl', SegmentedControl)
app.component('Segment', Segment)
app.component('ImoSpinner', ImoSpinner)

// CSS
import('./assets/style/main.css')
import('sweetalert2/dist/sweetalert2.min.css')
import('vue-loading-overlay/dist/css/index.css')

// Auth guard
const authStore = useAuthStore()
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.auth)
  const requiresAdmin = to.matched.some(record => record.meta.admin)

  if (requiresAuth) {
    if (!authStore.isLoggedIn) {
      return next({ path: '/login', query: { next: to.fullPath } })
    }
    if (requiresAdmin && !authStore.isSuperuser) {
      return next({ path: '/dashboard' })
    }
    if (!authStore.isValidated && !authStore.isSuperuser) {
      try {
        Swal.fire({
          title: 'Atenção!',
          text: 'Entre em contato com o suporte.',
          icon: 'info',
        })
        await authStore.logout()
        return next({ path: '/login' })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to logout:', err)
        return next(false)
      }
    }
  }

  const exceptionRedirect = urlNextExceptions.some(exception => to.query.next?.includes(exception))

  if (exceptionRedirect) {
    to.query.next = generateCleanNextQuery(to.query.next)
    return next(to)
  }

  return next()
})

setupInterceptors()

// Error handler global
app.config.errorHandler = (err, vm, info) => {
  // eslint-disable-next-line no-console
  console.error('[Vue Error]:', err, info)
  // aqui você pode usar Sentry, LogRocket, Hotjar etc
}

app.mount('#app')
